import { Link } from "react-router-dom";
import { ArrowRight, Star, Shield, Clock, Award, Wrench } from "lucide-react";
import { SERVICES, TESTIMONIALS } from "@/data/siteData";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/5e0315af-7eff-4b2c-b97d-580f25e1b3ed/images/752090a6a3af09bf2c253abe22a4b0b24b37f9e43ba6afbb48523528295efe52.png";
const TEXTURE_BG = "https://static.prod-images.emergentagent.com/jobs/5e0315af-7eff-4b2c-b97d-580f25e1b3ed/images/196290de01abe9ebea5fdeea0641e482fe7de4ab4d40b13ce204f63c67f076f3.png";

const STATS = [
  { value: "500+", label: "Projects Completed" },
  { value: "15+", label: "Years Experience" },
  { value: "100%", label: "Satisfaction Rate" },
  { value: "50+", label: "5-Star Reviews" },
];

const WHY_US = [
  { icon: Shield, title: "Licensed & Insured", desc: "Fully licensed professionals with comprehensive insurance for your peace of mind." },
  { icon: Clock, title: "On-Time Delivery", desc: "We respect your schedule. Projects completed on time, every time, with no hidden delays." },
  { icon: Award, title: "Premium Quality", desc: "We use only top-grade materials from trusted Australian and international suppliers." },
  { icon: Wrench, title: "One Team, Every Job", desc: "Flooring, handyman, outdoor — one trusted team for all your property needs." },
];

export default function HomePage() {
  const flooringServices = SERVICES.filter(s => s.category === "flooring");
  const handymanServices = SERVICES.filter(s => s.category === "handyman");
  const testimonials = TESTIMONIALS;

  return (
    <main data-testid="home-page">
      {/* Hero */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center"
      >
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center py-32">
          <p className="font-sans uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-6 opacity-0 animate-fade-in-up stagger-1">
            Flooring & Handyman Experts
          </p>
          <h1
            data-testid="hero-heading"
            className="font-serif text-4xl sm:text-5xl lg:text-7xl tracking-tight text-white leading-tight mb-6 opacity-0 animate-fade-in-up stagger-2"
          >
            Transform Your Space<br />
            with the <span className="gold-gradient-text">Solid Step</span>
          </h1>
          <p className="font-sans text-base sm:text-lg text-[#A3A3A3] max-w-2xl mx-auto mb-10 opacity-0 animate-fade-in-up stagger-3">
            From Floor to Finish — We've Got You Covered. Expert flooring installation, handyman services, and outdoor maintenance for homes and businesses across Australia.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 opacity-0 animate-fade-in-up stagger-4">
            <Link
              to="/contact"
              data-testid="hero-cta-quote"
              className="bg-[#D4AF37] text-black font-semibold px-10 py-4 text-sm uppercase tracking-wider hover:bg-[#F0C74A] transition-colors inline-flex items-center gap-2"
            >
              Get a Free Quote
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-services"
              className="border border-[#D4AF37] text-white font-semibold px-10 py-4 text-sm uppercase tracking-wider hover:bg-[#D4AF37]/10 transition-colors"
            >
              Our Services
            </Link>
          </div>
        </div>

        {/* Stats bar */}
        <div className="absolute bottom-0 left-0 right-0 bg-[#0a0a0a]/90 backdrop-blur-md border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            {STATS.map((stat, i) => (
              <div key={i} className="text-center" data-testid={`stat-${i}`}>
                <p className="font-serif text-2xl sm:text-3xl text-[#D4AF37] font-bold">{stat.value}</p>
                <p className="font-sans text-xs uppercase tracking-wider text-[#A3A3A3] mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Flooring Services */}
      <section data-testid="flooring-services-section" className="py-20 md:py-28 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="font-sans uppercase text-xs tracking-[0.2em] text-[#D4AF37] mb-4">Flooring</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-white mb-4">Flooring Services</h2>
            <p className="font-sans text-base text-[#A3A3A3] max-w-xl mx-auto">
              Premium flooring solutions — from supply to expert installation for every room in your home or business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {flooringServices.map((service) => (
              <Link
                key={service.id}
                to={`/services/${service.id}`}
                data-testid={`service-card-${service.id}`}
                className="group relative overflow-hidden bg-[#121212] border border-white/5 transition-all duration-500 hover:border-[#D4AF37]/40 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(212,175,55,0.1)]"
              >
                <div className="relative overflow-hidden h-56">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-serif text-xl text-white mb-2 group-hover:text-[#D4AF37] transition-colors">
                    {service.title}
                  </h3>
                  <p className="font-sans text-sm text-[#A3A3A3] line-clamp-2">{service.short_description}</p>
                  <span className="inline-flex items-center gap-1 text-[#D4AF37] text-xs uppercase tracking-wider mt-3 font-medium">
                    Learn More <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Handyman Services */}
      <section data-testid="handyman-services-section" className="py-20 md:py-28 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="font-sans uppercase text-xs tracking-[0.2em] text-[#D4AF37] mb-4">Handyman</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-white mb-4">Handyman Services</h2>
            <p className="font-sans text-base text-[#A3A3A3] max-w-xl mx-auto">
              From outdoor maintenance to interior repairs — one trusted team for every job around your property.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {handymanServices.map((service) => (
              <Link
                key={service.id}
                to={`/services/${service.id}`}
                data-testid={`service-card-${service.id}`}
                className="group relative overflow-hidden bg-[#121212] border border-white/5 transition-all duration-500 hover:border-[#D4AF37]/40 hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(212,175,55,0.1)]"
              >
                <div className="relative overflow-hidden h-52">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="font-serif text-lg text-white mb-1 group-hover:text-[#D4AF37] transition-colors">
                    {service.title}
                  </h3>
                  <p className="font-sans text-xs text-[#A3A3A3] line-clamp-2">{service.short_description}</p>
                  <span className="inline-flex items-center gap-1 text-[#D4AF37] text-xs uppercase tracking-wider mt-2 font-medium">
                    Learn More <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section
        data-testid="why-choose-section"
        className="relative py-20 md:py-32"
      >
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${TEXTURE_BG})` }}
        />
        <div className="absolute inset-0 texture-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-sans uppercase text-xs tracking-[0.2em] text-[#D4AF37] mb-4">Why Solid Step</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-white">The Difference Is in the Detail</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {WHY_US.map((item, i) => (
              <div
                key={i}
                data-testid={`why-us-${i}`}
                className="bg-white/5 backdrop-blur-xl border border-white/10 p-8 text-center transition-all duration-300 hover:border-[#D4AF37]/30 hover:bg-white/[0.07]"
              >
                <div className="w-14 h-14 mx-auto mb-5 border border-[#D4AF37]/40 flex items-center justify-center">
                  <item.icon className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h3 className="font-serif text-lg text-white mb-3">{item.title}</h3>
                <p className="font-sans text-sm text-[#A3A3A3] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" className="py-20 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-sans uppercase text-xs tracking-[0.2em] text-[#D4AF37] mb-4">Testimonials</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-white">What Our Clients Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((t) => (
              <div
                key={t.id}
                data-testid={`testimonial-${t.id}`}
                className="bg-[#121212] border border-white/5 p-8 transition-all duration-300 hover:border-[#D4AF37]/30"
              >
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
                  ))}
                </div>
                <p className="font-sans text-[#A3A3A3] text-sm leading-relaxed mb-6 italic">
                  "{t.text}"
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-serif text-white text-base">{t.name}</p>
                    <p className="font-sans text-xs text-[#A3A3A3]">{t.location}</p>
                  </div>
                  <span className="text-xs text-[#D4AF37] uppercase tracking-wider font-sans">{t.service}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="py-20 md:py-28 bg-[#121212] border-y border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="font-sans text-base text-[#A3A3A3] mb-8 max-w-xl mx-auto">
            Whether it's new floors, a handyman job, or outdoor maintenance — get in touch for a free, no-obligation quote.
          </p>
          <Link
            to="/contact"
            data-testid="cta-button"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-10 py-4 text-sm uppercase tracking-wider hover:bg-[#F0C74A] transition-colors"
          >
            Request Your Free Quote
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </main>
  );
}
